'use client';

import type { HTMLAttributes } from 'react';
import React, { useId } from 'react';
import styled from 'styled-components';
import type { Placement } from '@popperjs/core';
import { theme } from '@frameio/vapor-theme';

const ArrowContainer = styled.div`
  background-repeat: no-repeat;
  position: absolute;

  &[data-position*='top'] {
    height: 7px;
    left: 50%;
    top: 100%;
    width: 27px;
    transform: translateX(-50%);
  }

  &[data-position*='bottom'] {
    bottom: 100%;
    height: 7px;
    left: 50%;
    width: 27px;
    transform: translateX(-50%);
  }

  &[data-position='bottom-start'],
  &[data-position='top-start'] {
    left: 0;
    transform: none;
  }

  &[data-position='bottom-end'],
  &[data-position='top-end'] {
    left: unset;
    right: 0;
    transform: none;
  }

  &[data-position*='right'] {
    height: 27px;
    right: 100%;
    top: 50%;
    width: 7px;
    transform: translateY(-50%);
  }

  &[data-position*='left'] {
    height: 27px;
    left: calc(100% - 1px);
    top: 50%;
    width: 7px;
    transform: translateY(-50%);
  }

  &[data-position='right-start'],
  &[data-position='left-start'] {
    top: 0;
    transform: none;
  }

  &[data-position='right-end'],
  &[data-position='left-end'] {
    bottom: 0;
    top: unset;
    transform: none;
  }

  /* Override transforms when positioned by floating UI */
  &[data-floating-ui-positioned] {
    transform: none;
  }

  svg {
    float: left;
  }
`;

export type ArrowProps = {
  position?: Placement;
  className?: string;
} & HTMLAttributes<HTMLDivElement>;

export const Arrow = React.forwardRef<any, ArrowProps>(
  ({ position, className, ...other }, ref) => {
    const renderArrow = () => {
      switch (position) {
        case 'top':
        case 'top-start':
        case 'top-end':
          return <ArrowDown />;
        case 'bottom':
        case 'bottom-start':
        case 'bottom-end':
          return <ArrowUp />;
        case 'left':
        case 'left-start':
        case 'left-end':
          return <ArrowRight />;
        case 'right':
        case 'right-start':
        case 'right-end':
          return <ArrowLeft />;
        default:
          return null;
      }
    };

    return (
      <ArrowContainer
        ref={ref}
        className={className}
        data-popper-arrow
        data-position={position}
        {...other}
      >
        {renderArrow()}
      </ArrowContainer>
    );
  }
);

function ArrowUp() {
  const id = useId();
  return (
    <svg fill="none" height={7} viewBox="0 0 26 7" width={26}>
      <g clipPath={`url(#fio-arrow-up-filter-${id})`}>
        <path
          d="M10.453.91l-4.3 4.014A4 4 0 013.423 6h18.154a4 4 0 01-2.73-1.076L14.547.91a3 3 0 00-4.094 0z"
          fill={theme.colors.border.tertiary}
        />
        <path
          d="M11.135 1.974l-4.232 3.95A4 4 0 014.173 7h16.653a4 4 0 01-2.729-1.076l-4.233-3.95a2 2 0 00-2.729 0z"
          fill={theme.colors.components.tooltip.background}
        />
      </g>
      <defs>
        <clipPath id={`fio-arrow-up-filter-${id}`}>
          <path
            d="M0 0h25.5v7H0z"
            fill="#fff"
            transform="matrix(-1 0 0 1 25.5 0)"
          />
        </clipPath>
      </defs>
    </svg>
  );
}

function ArrowDown() {
  const id = useId();
  return (
    <svg fill="none" height={7} viewBox="0 0 27 7" width={27}>
      <g clipPath={`url(#fio-arrow-down-filter-${id})`}>
        <path
          d="M11.234 6.089l-4.3-4.014A4 4 0 004.204 1h18.154a4 4 0 00-2.73 1.075l-4.3 4.014a3 3 0 01-4.094 0z"
          fill={theme.colors.border.tertiary}
        />
        <path
          d="M11.916 5.026L7.48.885A3.293 3.293 0 005.233 0H21.33c-.834 0-1.637.316-2.247.885l-4.436 4.14a2 2 0 01-2.73 0z"
          fill={theme.colors.components.tooltip.background}
        />
      </g>
      <defs>
        <clipPath id={`fio-arrow-down-filter-${id}`}>
          <path
            d="M26.281 7h25.5v7h-25.5z"
            fill="#fff"
            transform="rotate(-180 26.281 7)"
          />
        </clipPath>
      </defs>
    </svg>
  );
}

function ArrowLeft() {
  const id = useId();
  return (
    <svg fill="none" height={26} viewBox="0 0 8 26" width={8}>
      <g clipPath={`url(#fio-arrow-left-filter-${id})`}>
        <path
          d="M1.41 15.046l4.014 4.3a4 4 0 011.076 2.73V3.923a4 4 0 01-1.076 2.73l-4.014 4.3a3 3 0 000 4.094z"
          fill={theme.colors.border.tertiary}
        />
        <path
          d="M2.474 14.364l3.95 4.233a4 4 0 011.076 2.73V4.672a4 4 0 01-1.076 2.73l-3.95 4.232a2 2 0 000 2.73z"
          fill={theme.colors.components.tooltip.background}
        />
      </g>
      <defs>
        <clipPath id={`fio-arrow-left-filter-${id}`}>
          <path
            d="M0 0h25.5v7H0z"
            fill="#fff"
            transform="matrix(0 1 1 0 .5 0)"
          />
        </clipPath>
      </defs>
    </svg>
  );
}

function ArrowRight() {
  const id = useId();
  return (
    <svg fill="none" height={26} viewBox="0 0 8 26" width={8}>
      <g clipPath={`url(#fio-arrow-right-filter-${id})`}>
        <path
          d="M6.59 15.046l-4.014 4.3a4 4 0 00-1.076 2.73V3.923a4 4 0 001.076 2.73l4.014 4.3a3 3 0 010 4.094z"
          fill={theme.colors.border.tertiary}
        />
        <path
          d="M5.526 14.364l-3.95 4.233A4 4 0 00.5 21.327V4.672a4 4 0 001.076 2.73l3.95 4.232a2 2 0 010 2.73z"
          fill={theme.colors.components.tooltip.background}
        />
      </g>
      <defs>
        <clipPath id={`fio-arrow-right-filter-${id}`}>
          <path d="M7.5 0H33v7H7.5z" fill="#fff" transform="rotate(90 7.5 0)" />
        </clipPath>
      </defs>
    </svg>
  );
}
